import { useState } from "react";
import { authFetch } from "../api";

// Compact analysis-page feedback on the AI rent. Writes a scoped insight the
// predictor reads back (unit- or building-level). Design is intentionally short:
// feedback is rare, so we optimize for quality-per-submission, not volume.

const VERDICTS = [
  { key: "too_low", label: "Too low" },
  { key: "about_right", label: "About right" },
  { key: "too_high", label: "Too high" },
];

const REASONS = [
  { key: "stale_comps", label: "Stale comps" },
  { key: "reno_wrong", label: "Reno level" },
  { key: "missing_feature", label: "Missing feature" },
  { key: "floor_line", label: "Floor / line" },
  { key: "sqft_wrong", label: "Sqft" },
  { key: "building_specific", label: "Building-specific" },
  { key: "seasonality", label: "Seasonality" },
  { key: "other", label: "Other" },
];

const chip = (active) => ({
  padding: "5px 11px",
  borderRadius: 16,
  border: `1px solid ${active ? "#2563eb" : "#d1d5db"}`,
  background: active ? "#eff6ff" : "#fff",
  color: active ? "#1d4ed8" : "#374151",
  fontSize: 13,
  cursor: "pointer",
  userSelect: "none",
});

export default function PredictionFeedback({ listingId, aiRent }) {
  const [verdict, setVerdict] = useState(null);
  const [scope, setScope] = useState("property"); // 'property' | 'building'
  const [correctedRent, setCorrectedRent] = useState("");
  const [percentage, setPercentage] = useState("");
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const toggleReason = (key) =>
    setReasons((r) => (r.includes(key) ? r.filter((x) => x !== key) : [...r, key]));

  const showCorrection = verdict === "too_low" || verdict === "too_high";

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        verdict,
        scope,
        reasons: reasons.length ? reasons : null,
        reason_text: note.trim() || null,
        corrected_rent:
          scope === "property" && correctedRent ? Number(correctedRent) : null,
        percentage: scope === "building" && percentage ? Number(percentage) : null,
      };
      const res = await authFetch(`/predict/sale/${listingId}/feedback`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res && res.success) setDone(true);
      else setError((res && res.detail) || "Could not save feedback");
    } catch (e) {
      setError("Could not save feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ ...cardStyle, color: "#166534", background: "#f0fdf4", borderColor: "#bbf7d0" }}>
        ✓ Thanks — your feedback was saved{" "}
        {(correctedRent || percentage) ? "and the prediction will refresh with it." : "."}
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>Is this rent right?</span>
        {aiRent ? (
          <span style={{ fontSize: 12, color: "#6b7280" }}>AI: ${Number(aiRent).toLocaleString()}/mo</span>
        ) : null}
      </div>

      {/* Verdict */}
      <div style={{ display: "flex", gap: 8, marginBottom: showCorrection || verdict === "about_right" ? 12 : 4 }}>
        {VERDICTS.map((v) => (
          <button key={v.key} onClick={() => setVerdict(v.key)} style={verdictBtn(verdict === v.key)}>
            {v.label}
          </button>
        ))}
      </div>

      {verdict && verdict !== "about_right" && (
        <>
          {/* Scope */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <span style={labelStyle}>Applies to</span>
            <button onClick={() => setScope("property")} style={chip(scope === "property")}>This unit</button>
            <button onClick={() => setScope("building")} style={chip(scope === "building")}>This building</button>
          </div>

          {/* Correction */}
          <div style={{ marginBottom: 12 }}>
            {scope === "property" ? (
              <label style={{ fontSize: 13, color: "#374151" }}>
                It should be&nbsp;
                <span style={{ color: "#6b7280" }}>$</span>
                <input
                  type="number"
                  value={correctedRent}
                  onChange={(e) => setCorrectedRent(e.target.value)}
                  placeholder="rent/mo"
                  style={inputStyle}
                />
              </label>
            ) : (
              <label style={{ fontSize: 13, color: "#374151" }}>
                Adjust building by&nbsp;
                <input
                  type="number"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                  placeholder="+/- %"
                  style={inputStyle}
                />
                &nbsp;%
              </label>
            )}
          </div>

          {/* Reasons */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ ...labelStyle, marginBottom: 6 }}>Why? (optional)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {REASONS.map((r) => (
                <span key={r.key} onClick={() => toggleReason(r.key)} style={chip(reasons.includes(r.key))}>
                  {r.label}
                </span>
              ))}
            </div>
          </div>

          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)"
            style={{ ...inputStyle, width: "100%", marginBottom: 12, boxSizing: "border-box" }}
          />
        </>
      )}

      {error && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>{error}</div>}

      {verdict && (
        <button onClick={submit} disabled={submitting} style={submitBtn(submitting)}>
          {submitting ? "Saving…" : "Submit feedback"}
        </button>
      )}
    </div>
  );
}

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 16,
  background: "#fafafa",
  fontSize: 13,
};
const labelStyle = { fontSize: 12, color: "#6b7280", fontWeight: 500 };
const inputStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "5px 8px",
  fontSize: 13,
  width: 110,
};
const verdictBtn = (active) => ({
  flex: 1,
  padding: "7px 0",
  borderRadius: 7,
  border: `1px solid ${active ? "#2563eb" : "#d1d5db"}`,
  background: active ? "#2563eb" : "#fff",
  color: active ? "#fff" : "#374151",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
});
const submitBtn = (disabled) => ({
  padding: "8px 16px",
  borderRadius: 7,
  border: "none",
  background: disabled ? "#9ca3af" : "#111827",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  cursor: disabled ? "default" : "pointer",
});
